import React, { useState } from "react";
import { appendLeadSource } from "../lib/leadSource";
import axios from "axios";
import { promoNoStackLine } from "../lib/promo";
import { FileText, X, Loader2, CheckCircle2, Scale } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX = 3;
const input = "w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#3b9dff] focus:outline-none text-sm";

export const PriceMatchUpload = ({ source, defaults = {}, details = "", promo }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: defaults.name || "", phone: defaults.phone || "", email: defaults.email || "", message: "" });
  const [files, setFiles] = useState([]);
  const [st, setSt] = useState({ sending: false, done: false, error: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!files.length) return setSt({ ...st, error: "Please attach the written quote." });
    setSt({ sending: true, done: false, error: "" });
    const fd = new FormData();
    fd.append("kind", "price_match");
    fd.append("name", form.name); fd.append("phone", form.phone); fd.append("email", form.email);
    fd.append("message", `[${source}] ${form.message}${details ? `\n\n${details}` : ""}`);
    files.forEach((f) => fd.append("photos", f));
    try {
      await axios.post(`${API}/photo-quote`, appendLeadSource(fd));
      setSt({ sending: false, done: true, error: "" });
    } catch (err) {
      setSt({ sending: false, done: false, error: (typeof err.response?.data?.detail === "string" && err.response.data.detail) || "Upload failed. Please try again or text (252) 545-0011." });
    }
  };

  return (
    <div data-testid={`price-match-${source}`} className="rounded-2xl border border-dashed border-[#3b9dff]/50 bg-white p-5">
      <button type="button" onClick={() => { if (!open) setForm((f) => ({ ...f, name: f.name || defaults.name || "", phone: f.phone || defaults.phone || "", email: f.email || defaults.email || "" })); setOpen(!open); }} data-testid={`price-match-${source}-toggle`} className="w-full flex items-center gap-3 text-left">
        <span className="w-10 h-10 shrink-0 rounded-xl bg-[#eef4ff] flex items-center justify-center"><Scale className="w-5 h-5 text-[#3b9dff]" /></span>
        <span className="flex-1">
          <span className="block font-bold text-[#0a1f44]">Have a written quote? Upload it</span>
          <span className="block text-sm text-slate-500">We will match or beat any written quote on the same TV.</span>
          {promo && <span data-testid={`price-match-${source}-nostack`} className="block text-xs text-slate-400 mt-0.5">{promoNoStackLine(promo)}</span>}
        </span>
        <span className="text-sm font-semibold text-[#3b9dff]">{open ? "Close" : "Upload"}</span>
      </button>
      {open && (st.done ? (
        <p data-testid={`price-match-${source}-success`} className="mt-4 flex items-center gap-2 text-sm text-emerald-700"><CheckCircle2 className="w-5 h-5" /> Got it. We will review the quote and get back to you by text or call.</p>
      ) : (
        <form onSubmit={submit} className="mt-4 space-y-3">
          <label className="flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-200 px-4 py-5 text-sm font-semibold text-[#0a1f44] cursor-pointer hover:border-[#3b9dff] transition-colors">
            <FileText className="w-5 h-5 text-[#3b9dff]" /> Attach the written quote (photo or PDF, up to {MAX})
            <input type="file" multiple accept="image/*,.heic,.heif,application/pdf,.pdf" data-testid={`price-match-${source}-input`} className="hidden"
              onChange={(e) => { setFiles([...files, ...Array.from(e.target.files)].slice(0, MAX)); setSt({ ...st, error: "" }); e.target.value = ""; }} />
          </label>
          {files.length > 0 && (
            <ul className="flex flex-wrap gap-2">
              {files.map((f, i) => (
                <li key={`${f.name}-${i}`} className="flex items-center gap-2 pl-3 pr-1 py-1 rounded-full bg-[#f5f8ff] border border-slate-200 text-xs text-slate-600">
                  <span className="max-w-[160px] truncate">{f.name}</span>
                  <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} className="p-1 rounded-full hover:bg-white"><X className="w-3 h-3" /></button>
                </li>
              ))}
            </ul>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            <input required placeholder="Name *" value={form.name} onChange={set("name")} data-testid={`price-match-${source}-name`} className={input} />
            <input required type="tel" placeholder="Phone *" value={form.phone} onChange={set("phone")} data-testid={`price-match-${source}-phone`} className={input} />
          </div>
          <input placeholder="TV model on the quote (optional)" value={form.message} onChange={set("message")} data-testid={`price-match-${source}-notes`} className={input} />
          {st.error && <p data-testid={`price-match-${source}-error`} className="text-sm text-red-600">{st.error}</p>}
          <button type="submit" disabled={st.sending} data-testid={`price-match-${source}-submit`} className="w-full flex items-center justify-center gap-2 py-3 rounded-full bg-[#0a1f44] hover:bg-[#123a7a] text-white font-bold disabled:opacity-60 transition-colors">
            {st.sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading</> : "Send for a price match"}
          </button>
        </form>
      ))}
    </div>
  );
};
